import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import apiClient from '@/lib/api';
import { Chrome, MessageSquare, Github, Apple, Gamepad2, Settings, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const PROVIDER_ICONS = {
  google: Chrome,
  discord: MessageSquare,
  github: Github,
  microsoft: Chrome,
  apple: Apple,
  steam: Gamepad2,
  minecraft: Gamepad2,
};

const AdminProvidersPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { fetchProviders(); }, []);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/providers');
      setProviders(res.data);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const toggleEnabled = async (provider) => {
    try {
      await apiClient.patch(`/admin/providers/${provider.provider_id}`, {
        enabled: !provider.enabled
      });
      toast.success(`${provider.name} ${!provider.enabled ? 'enabled' : 'disabled'}`);
      fetchProviders();
    } catch (error) {
      toast.error('Failed to update provider');
    }
  };

  const openConfigure = (provider) => {
    setSelectedProvider(provider);
    setFormData({
      client_id: provider.client_id || '',
      client_secret: '',
      redirect_uri: provider.redirect_uri || ''
    });
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      const updates = {};
      if (formData.client_id) updates.client_id = formData.client_id;
      if (formData.client_secret) updates.client_secret = formData.client_secret;
      if (formData.redirect_uri) updates.redirect_uri = formData.redirect_uri;
      
      await apiClient.patch(`/admin/providers/${selectedProvider.provider_id}`, updates);
      toast.success('Provider configuration saved');
      setSelectedProvider(null);
      fetchProviders();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="w-10 h-10 border-4 border-pista border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-providers-page">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {providers.map((provider) => {
          const Icon = PROVIDER_ICONS[provider.type] || Chrome;
          return (
            <Card key={provider.provider_id} data-testid={`provider-${provider.type}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-lg bg-secondary flex items-center justify-center">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold flex items-center space-x-2">
                        <span>{provider.name}</span>
                        {provider.mocked && (
                          <Badge variant="secondary" className="text-xs">MOCK</Badge>
                        )}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{provider.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={provider.enabled}
                    onCheckedChange={() => toggleEnabled(provider)}
                    data-testid={`toggle-provider-${provider.type}`}
                  />
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Client ID</span>
                    {provider.client_id ? (
                      <span className="font-mono text-xs">{provider.client_id.substring(0, 20)}...</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">Not configured</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Status</span>
                    <span className="flex items-center space-x-1">
                      {provider.enabled ? (
                        <>
                          <CheckCircle className="h-3 w-3 text-pista" />
                          <span className="text-pista text-xs font-medium">Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground text-xs">Disabled</span>
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {provider.type !== 'google' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => openConfigure(provider)}
                    data-testid={`configure-${provider.type}`}
                  >
                    <Settings className="h-3 w-3 mr-2" />
                    Configure
                  </Button>
                )}
                {provider.type === 'google' && (
                  <p className="text-xs text-center text-muted-foreground italic">
                    Managed by Emergent
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Configure Dialog */}
      <Dialog open={!!selectedProvider} onOpenChange={(open) => !open && setSelectedProvider(null)}>
        <DialogContent data-testid="provider-config-dialog">
          {selectedProvider && (
            <>
              <DialogHeader>
                <DialogTitle>Configure {selectedProvider.name}</DialogTitle>
                <DialogDescription>
                  Enter OAuth credentials to enable real integration. Leave empty to keep in mock mode.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div>
                  <Label htmlFor="client-id">Client ID</Label>
                  <Input
                    id="client-id"
                    data-testid="config-client-id"
                    placeholder="Your OAuth client ID"
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="client-secret">Client Secret</Label>
                  <Input
                    id="client-secret"
                    data-testid="config-client-secret"
                    type="password"
                    placeholder="Your OAuth client secret (leave empty to keep existing)"
                    value={formData.client_secret}
                    onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="redirect-uri">Redirect URI</Label>
                  <Input
                    id="redirect-uri"
                    data-testid="config-redirect-uri"
                    placeholder="https://account.lostdrkc.in/auth/callback"
                    value={formData.redirect_uri}
                    onChange={(e) => setFormData({ ...formData, redirect_uri: e.target.value })}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedProvider(null)}>Cancel</Button>
                <Button onClick={saveConfig} disabled={isSaving} data-testid="save-provider-config">
                  {isSaving ? 'Saving...' : 'Save Configuration'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProvidersPage;
